import React, { useState, useEffect, useCallback, useRef } from 'react';
import en from '../../../translations/en.json';
import LabelAvatarStack from '../../../components/shared/LabelAvatarStack';
import { apiClient } from '../../../api/apiClient';
import { GET_ARTIST_SONGS_URL } from '../../../api/artist';
import EmptyState from '../../../components/shared/EmptyState';
import Loader from '../../../components/shared/Loader';
import SharedHeader from '../../../components/shared/SharedHeader';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import { toast } from '../../../components/shared/toast';
import FilterPopup from '../../Songs/components/FilterPopup';
import SortPopup from '../../Songs/components/SortPopup';
import { filterSongsByOwnership } from '../../Songs/utils/ownershipFilter';
import { formatConflictDisplay, hasConflict, formatOwnershipDisplay } from '../../../utils/songDataFormatters';

function SongsTab({ artist, onSelectSong }) {
  const [songs, setSongs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSort, setSelectedSort] = useState('Songs A-Z');
  const [filters, setFilters] = useState({
    status: null,
    conflict: null,
    ownership: null,
    version: null,
  });
  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const tableContainerRef = useRef(null);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const getStatusValue = (status) => {
    if (!status) return '';
    return status.toLowerCase() === 'active' ? '1' : '0';
  };

  const getConflictValue = (conflict) => {
    if (!conflict) return '';
    return conflict.toLowerCase() === 'yes' ? '1' : '0';
  };

  const handleFilterChange = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      status: null,
      conflict: null,
      ownership: null,
      version: null,
    });
    setSearchQuery('');
    setPage(1);
  };

  const fetchSongs = useCallback(async (pageNum, isAppend = false) => {
    if (!artist || !artist.id) return;
    if (isAppend) setLoadingMore(true);
    else setIsLoading(true);

    try {
      const queryParams = new URLSearchParams({
        page: pageNum,
        limit: 20,
        search: searchQuery,
        status: getStatusValue(filters.status),
        conflict: getConflictValue(filters.conflict),
        ownership: filters.ownership || '',
        versionType: filters.version || '',
        sort: selectedSort,
      }).toString();

      const res = await apiClient(`${GET_ARTIST_SONGS_URL(artist.id)}?${queryParams}`);
      if (res && res.songs) {
        const fetchedSongs = res.songs || [];
        const filteredSongs = filterSongsByOwnership(fetchedSongs, filters.ownership);
        setSongs(prev => isAppend ? [...prev, ...filteredSongs] : filteredSongs);
        setTotalCount(res.totalCount || 0);
        setHasMore(res.hasMore !== undefined ? res.hasMore : res.songs.length === 20);
      }
    } catch (err) {
      console.error('Failed to fetch artist songs:', err);
    } finally {
      setIsLoading(false);
      setLoadingMore(false);
    }
  }, [artist?.id, searchQuery, filters.status, filters.conflict, filters.ownership, filters.version, selectedSort]);

  useEffect(() => {
    fetchSongs(1, false);
  }, [artist?.id, searchQuery, filters.status, filters.conflict, filters.ownership, filters.version, selectedSort]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMore && !loadingMore && !isLoading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchSongs(nextPage, true);
    }
  };

  const getLabelName = (label) => {
    if (!label) return '';
    if (typeof label === 'string') return label;
    return label.name || label.labelName || label.display_name || label.recordLabelName || label.record_label_name || '';
  };

  const getSongRecordLabel = (song) => {
    const labelSource = song?.recordLabelName || song?.record_label_name || song?.recordLabel || song?.record_label;
    if (typeof labelSource === 'string' && labelSource.trim()) {
      return labelSource.trim();
    }

    const labelList = song?.labels || song?.recordLabels;
    if (Array.isArray(labelList) && labelList.length > 0) {
      return labelList.map(getLabelName).filter(Boolean).join(', ') || '—';
    }

    if (typeof labelList === 'string' && labelList.trim()) {
      return labelList.trim();
    }

    return '—';
  };

  const handleExportSongs = async () => {
    try {
      setIsExporting(true);
      await new Promise(resolve => setTimeout(resolve, 800));

      if (!artist || !artist.id) {
        toast.error('Artist information not available.');
        return;
      }

      // Fetch ALL filtered records from backend with high limit (not just the 20 loaded)
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 10000, // Fetch up to 10,000 records
        search: searchQuery,
        status: getStatusValue(filters.status),
        conflict: getConflictValue(filters.conflict),
        ownership: filters.ownership || '',
        versionType: filters.version || '',
        sort: selectedSort,
      }).toString();

      const response = await apiClient(`${GET_ARTIST_SONGS_URL(artist.id)}?${queryParams}`);
      
      if (response && response.songs && response.songs.length > 0) {
        const allSongs = response.songs;
        const headers = ['Song Name', 'Sinhala Name', 'Artist', 'Record Label', 'Lyrics', 'Music', 'ISRC Code', 'Version Type', 'Ownership', 'Conflicts'];
        const rows = allSongs.map(s => [
          s.name,
          s.nameSinhala || '',
          s.artist,
          getSongRecordLabel(s),
          s.lyrics,
          s.music,
          s.isrcCode,
          s.versionType,
          `${s.ownership}%`,
          s.conflicts || s.conflict || 'No'
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.setAttribute('href', URL.createObjectURL(blob));
        const formattedName = (artist?.name || 'artist').replace(/\s+/g, '_');
        link.setAttribute('download', `artist_songs_${formattedName}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Artist songs exported successfully! (${allSongs.length} records)`);
      } else {
        toast.error('No songs to export.');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export songs list.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="artist-songs-tab">
      <LoadingOverlay isLoading={isExporting} message={t('common.exportingMessage') || 'Exporting data...'} />
      <SharedHeader
        title=""
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('songs.searchPlaceholder') || 'Search songs...'}
        resultCount={totalCount || songs.length}
        resultLabel="Songs"
        showSortFilter={true}
        onSortClick={() => {
          setSortOpen(!sortOpen);
          setFilterOpen(false);
        }}
        onFilterClick={() => {
          setFilterOpen(!filterOpen);
          setSortOpen(false);
        }}
        sortActive={sortOpen}
        filterActive={filterOpen}
        isSortApplied={selectedSort !== 'Songs A-Z'}
        isFilterApplied={filters.status !== null || filters.conflict !== null || filters.ownership !== null || filters.version !== null}
        onExportClick={handleExportSongs}
      />

      {/* Popups for sort and filter */}
      <div className="popup-anchor-container">
        {sortOpen && (
          <SortPopup
            selectedSort={selectedSort}
            onSelect={setSelectedSort}
            onClose={() => setSortOpen(false)}
            t={t}
          />
        )}
        {filterOpen && (
          <FilterPopup
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            onClose={() => setFilterOpen(false)}
          />
        )}
      </div>

      <div 
        className="songs-tab-container" 
        ref={tableContainerRef}
        onScroll={handleScroll}
        style={{ maxHeight: '600px', overflowY: 'auto', marginTop: '16px' }}
      >
        <table className="songs-tab-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>#</th>
              <th>{t('albumDetails.songsColSongName')}</th>
              <th>{t('albumDetails.songsColStatus')}</th>
              <th>{t('albumDetails.songsColArtist')}</th>
              <th>{t('albumDetails.songsColLyricsMusic')}</th>
              <th>{t('albumDetails.songsColLabels')}</th>
              <th>{t('albumDetails.songsColOwnership')}</th>
              <th>{t('albumDetails.songsColNotesCases')}</th>
              <th>{t('albumDetails.songsColConflicts')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="9" className="empty-state-td">
                  <Loader label="Fetching songs, please wait." />
                </td>
              </tr>
            ) : songs.length === 0 ? (
              <tr>
                <td colSpan="9" className="empty-state-td">
                  <EmptyState title="No Songs Found" subtitle="No songs match your filters." />
                </td>
              </tr>
            ) : (
              <>
                {songs.map((song, index) => (
                  <tr 
                    key={song.id || index}
                    onClick={() => onSelectSong && onSelectSong(song)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ fontWeight: '600', color: '#94a3b8' }}>{String(index + 1).padStart(2, '0')}</td>
                    <td>
                      <div className="song-name-cell">
                        <span className="song-title-sinhala">{song.nameSinhala || song.name}</span>
                        <span className="song-title-english">{song.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="song-status-pill active">
                        <span className="status-dot"></span>
                        <span className="status-text">{t('albumDetails.songsStatusActive')}</span>
                      </div>
                    </td>
                    <td>
                      <div className="song-artists-cell">
                        <span className="artist-main">{song.artist || artist.name || 'Unknown Artist'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="song-lyrics-music-cell">
                        <div className="lyrics-text" style={{ fontSize: '13px', fontWeight: '500', color: '#1e293b' }}>{song.lyrics || '—'}</div>
                        <div className="music-text" style={{ fontSize: '12px', color: '#64748b' }}>{song.music || '—'}</div>
                      </div>
                    </td>
                    <td>
                      <LabelAvatarStack labels={song.labels || song.recordLabels} />
                    </td>
                    <td>
                      <span className="ownership-text">{formatOwnershipDisplay(song.ownership !== undefined ? song.ownership : (song.ownershipPercentage !== undefined ? song.ownershipPercentage : 0))}</span>
                    </td>
                    <td>
                      <span className="notes-cases-text">{song.notes || t('albumDetails.songsNoCasesNotes')}</span>
                    </td>
                    <td>
                      <span className={`conflicts-badge ${hasConflict(song.conflict || song.conflicts) ? 'conflict' : 'no-conflict'}`}>
                        {formatConflictDisplay(song.conflict || song.conflicts)}
                      </span>
                    </td>
                  </tr>
                ))}
                {loadingMore && (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '16px', color: '#64748b', fontSize: '13px' }}>
                      Loading more songs...
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SongsTab;
